import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Check, RotateCcw, Search, Sparkles, X } from "lucide-react";
import { cocktailMenu, cocktailMenuFilters } from "@/craftCocktails";
import { SectionLabel } from "./SectionLabel";

// Synthesised vault unlock: a mechanical clunk on turn, then a bell chime as the doors open.
function playUnlockSound() {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    const ctx = new AC();
    const now = ctx.currentTime;
    const master = ctx.createGain();
    master.gain.value = 0.6;
    master.connect(ctx.destination);

    // Low thud
    const thud = ctx.createOscillator();
    const thudGain = ctx.createGain();
    thud.type = "sine";
    thud.frequency.setValueAtTime(150, now);
    thud.frequency.exponentialRampToValueAtTime(52, now + 0.18);
    thudGain.gain.setValueAtTime(0.0001, now);
    thudGain.gain.exponentialRampToValueAtTime(0.55, now + 0.012);
    thudGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.38);
    thud.connect(thudGain).connect(master);
    thud.start(now);
    thud.stop(now + 0.42);

    // Mechanical clunk (filtered noise burst)
    const size = Math.floor(ctx.sampleRate * 0.2);
    const buffer = ctx.createBuffer(1, size, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < size; i += 1) data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / size, 2.2);
    const noise = ctx.createBufferSource();
    noise.buffer = buffer;
    const noiseFilter = ctx.createBiquadFilter();
    noiseFilter.type = "lowpass";
    noiseFilter.frequency.value = 420;
    const noiseGain = ctx.createGain();
    noiseGain.gain.setValueAtTime(0.32, now);
    noiseGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.17);
    noise.connect(noiseFilter).connect(noiseGain).connect(master);
    noise.start(now);

    // Deep mechanical clunk as the doors swing open (~0.55s later): heavy bolt/latch
    const clunkAt = now + 0.55;
    const boltThud = ctx.createOscillator();
    const boltGain = ctx.createGain();
    boltThud.type = "sine";
    boltThud.frequency.setValueAtTime(120, clunkAt);
    boltThud.frequency.exponentialRampToValueAtTime(40, clunkAt + 0.22);
    boltGain.gain.setValueAtTime(0.0001, clunkAt);
    boltGain.gain.exponentialRampToValueAtTime(0.6, clunkAt + 0.015);
    boltGain.gain.exponentialRampToValueAtTime(0.0001, clunkAt + 0.5);
    boltThud.connect(boltGain).connect(master);
    boltThud.start(clunkAt);
    boltThud.stop(clunkAt + 0.55);

    const clunkSize = Math.floor(ctx.sampleRate * 0.28);
    const clunkBuffer = ctx.createBuffer(1, clunkSize, ctx.sampleRate);
    const clunkData = clunkBuffer.getChannelData(0);
    for (let i = 0; i < clunkSize; i += 1) clunkData[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / clunkSize, 1.8);
    const clunkNoise = ctx.createBufferSource();
    clunkNoise.buffer = clunkBuffer;
    const clunkFilter = ctx.createBiquadFilter();
    clunkFilter.type = "lowpass";
    clunkFilter.frequency.value = 300;
    const clunkNoiseGain = ctx.createGain();
    clunkNoiseGain.gain.setValueAtTime(0.4, clunkAt);
    clunkNoiseGain.gain.exponentialRampToValueAtTime(0.0001, clunkAt + 0.24);
    clunkNoise.connect(clunkFilter).connect(clunkNoiseGain).connect(master);
    clunkNoise.start(clunkAt);

    window.setTimeout(() => ctx.close(), 3200);
  } catch (err) {
    console.debug("Vault unlock sound unavailable:", err);
  }
}

export default function CocktailVaultNext({ selectedCocktails, onToggleQuote }) {
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [flipped, setFlipped] = useState(() => new Set());

  // vault door state
  const [unlocked, setUnlocked] = useState(false); // grid fully revealed + interactive
  const [opening, setOpening] = useState(false); // doors mid-swing
  const sectionRef = useRef(null);
  const openTimer = useRef(null);
  const lastUnlockAt = useRef(0);

  const visibleCocktails = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return cocktailMenu.filter((cocktail) => {
      const matchesFilter = filter === "all" || cocktail.category === filter;
      const searchable = `${cocktail.name} ${cocktail.categoryLabel} ${cocktail.ingredients.join(" ")}`.toLowerCase();
      return matchesFilter && (!normalizedQuery || searchable.includes(normalizedQuery));
    });
  }, [filter, query]);

  const handleUnlock = useCallback(() => {
    if (opening || unlocked) return;
    lastUnlockAt.current = Date.now();
    playUnlockSound();
    setOpening(true);
    openTimer.current = window.setTimeout(() => setUnlocked(true), 1650);
  }, [opening, unlocked]);

  const relock = useCallback(() => {
    if (openTimer.current) window.clearTimeout(openTimer.current);
    setUnlocked(false);
    setOpening(false);
    setFlipped(new Set());
  }, []);

  // Re-lock the vault when the guest genuinely scrolls the section out of view.
  // Opening the doors grows the section ~3800px, which triggers browser scroll
  // anchoring; ignore relock briefly after an unlock so that reflow can't cancel it.
  useEffect(() => {
    const node = sectionRef.current;
    if (!node) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.intersectionRatio === 0 && Date.now() - lastUnlockAt.current > 2200) relock();
      },
      { threshold: [0] },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [relock]);

  useEffect(() => () => openTimer.current && window.clearTimeout(openTimer.current), []);

  const toggleFlip = (id) =>
    setFlipped((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  return (
    <section
      className="section cocktail-section vault-section"
      id="cocktails"
      ref={sectionRef}
      data-testid="cocktail-vault-section"
    >
      <div className="section-intro reveal-on-scroll">
        <SectionLabel number="03" testId="cocktail-vault-label">The cocktail vault</SectionLabel>
        <div className="intro-grid">
          <h2 data-testid="cocktail-vault-heading">Sixty ways to make an entrance.</h2>
          <p data-testid="cocktail-vault-description">Behind the vault door sits the complete craft menu — arranged by spirit and mood, with a zero-proof collection so every guest is considered. Turn the handle, then tap any pour to reveal its recipe.</p>
        </div>
      </div>

      <div className={`vault-stage${unlocked ? " is-unlocked" : ""}${opening ? " is-opening" : ""}`}>
        <AnimatePresence>
          {!unlocked && (
            <motion.div
              className="vault-door-scene"
              data-testid="vault-door-scene"
              initial={false}
              exit={{ opacity: 0, transition: { duration: 0.5, ease: "easeInOut" } }}
              aria-hidden={unlocked}
            >
              <div className="vault-door-frame">
              <motion.div
                className="vault-door vault-door-left"
                initial={{ rotateY: 0 }}
                animate={{ rotateY: opening ? -122 : 0 }}
                transition={{ duration: 1.15, delay: opening ? 0.55 : 0, ease: [0.76, 0, 0.24, 1] }}
              >
                <div className="vault-door-plate">
                  <span className="vault-rivet r-tl" /><span className="vault-rivet r-bl" />
                  <span className="vault-rivet r-tr" /><span className="vault-rivet r-br" />
                </div>
              </motion.div>

              <motion.div
                className="vault-door vault-door-right"
                initial={{ rotateY: 0 }}
                animate={{ rotateY: opening ? 122 : 0 }}
                transition={{ duration: 1.15, delay: opening ? 0.55 : 0, ease: [0.76, 0, 0.24, 1] }}
              >
                <div className="vault-door-plate">
                  <span className="vault-rivet r-tl" /><span className="vault-rivet r-bl" />
                  <span className="vault-rivet r-tr" /><span className="vault-rivet r-br" />
                </div>
              </motion.div>

              <div className="vault-door-center">
                <motion.button
                  type="button"
                  className="vault-wheel"
                  onClick={handleUnlock}
                  aria-label="Unlock the cocktail vault"
                  data-testid="vault-unlock-button"
                  animate={{ rotate: opening ? 315 : 0 }}
                  transition={{ duration: 0.85, ease: [0.65, 0, 0.35, 1] }}
                  whileHover={opening ? {} : { scale: 1.04 }}
                  whileTap={opening ? {} : { scale: 0.97 }}
                >
                  <span className="vault-wheel-ring" />
                  <span className="vault-wheel-spoke s-0" />
                  <span className="vault-wheel-spoke s-1" />
                  <span className="vault-wheel-spoke s-2" />
                  <span className="vault-wheel-hub">PS</span>
                </motion.button>
                <div className={`vault-door-copy${opening ? " is-hidden" : ""}`}>
                  <span className="vault-door-eyebrow">Members' pour list</span>
                  <button type="button" className="button button-primary vault-unlock-cta" onClick={handleUnlock} data-testid="vault-unlock-cta">
                    Unlock the vault <Sparkles size={12} />
                  </button>
                </div>
              </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="vault-inner" aria-hidden={!unlocked}>
          <div className="vault-tools" data-testid="cocktail-filter-bar">
            <div className="vault-search-wrap">
              <Search size={16} />
              <input
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search the vault…"
                aria-label="Search cocktail vault"
                data-testid="cocktail-search-input"
                tabIndex={unlocked ? 0 : -1}
              />
            </div>
            <div className="vault-count" data-testid="cocktail-result-count">{visibleCocktails.length} pours</div>
          </div>
          <div className="filter-list vault-filters">
            {cocktailMenuFilters.map((item) => (
              <button
                type="button"
                className={`filter-button${filter === item.id ? " is-active" : ""}`}
                key={item.id}
                onClick={() => setFilter(item.id)}
                data-testid={`cocktail-filter-${item.id}`}
                aria-pressed={filter === item.id}
                tabIndex={unlocked ? 0 : -1}
              >
                {item.label}
              </button>
            ))}
          </div>

          <div className="vault-grid">
            {visibleCocktails.length === 0 && (
              <div className="vault-empty" data-testid="vault-empty-state">
                <p>No pours match that search.</p>
                <button type="button" className="text-button" onClick={() => { setQuery(""); setFilter("all"); }} data-testid="vault-clear-search">
                  Clear the search <X size={14} />
                </button>
              </div>
            )}
            {visibleCocktails.map((cocktail, index) => {
              const inBrief = selectedCocktails.includes(cocktail.id);
              const isFlipped = flipped.has(cocktail.id);
              return (
                <div
                  className={`flip-card${isFlipped ? " is-flipped" : ""}${inBrief ? " is-selected" : ""}`}
                  key={cocktail.id}
                  data-testid={`cocktail-card-${cocktail.id}`}
                >
                  <div className="flip-card-inner">
                    {/* FRONT */}
                    <button
                      type="button"
                      className="flip-face flip-front"
                      onClick={() => unlocked && toggleFlip(cocktail.id)}
                      aria-label={`Reveal the recipe for ${cocktail.name}`}
                      data-testid={`cocktail-flip-front-${cocktail.id}`}
                      tabIndex={unlocked && !isFlipped ? 0 : -1}
                    >
                      <div className={`flip-front-media${cocktail.brightImage ? " is-bright" : ""}`}>
                        <img src={cocktail.image} alt={`${cocktail.name} specialty cocktail`} />
                      </div>
                      <span className="flip-front-category">{cocktail.categoryLabel}</span>
                      <span className="flip-front-index">{String(index + 1).padStart(2, "0")}</span>
                      <div className="flip-front-footer">
                        <h3 data-testid={`cocktail-name-${cocktail.id}`}>{cocktail.name}</h3>
                        <span className="flip-hint"><RotateCcw size={12} /> Tap for the recipe</span>
                      </div>
                      {inBrief && <span className="flip-brief-flag"><Check size={12} /> In brief</span>}
                    </button>

                    {/* BACK */}
                    <div className="flip-face flip-back" data-testid={`cocktail-recipe-${cocktail.id}`}>
                      <button
                        type="button"
                        className="flip-back-close"
                        onClick={() => toggleFlip(cocktail.id)}
                        aria-label={`Flip ${cocktail.name} back`}
                        data-testid={`cocktail-flip-back-${cocktail.id}`}
                        tabIndex={unlocked && isFlipped ? 0 : -1}
                      >
                        <X size={16} />
                      </button>
                      <div className="flip-back-scroll">
                        <div className="card-eyebrow">{cocktail.categoryLabel}</div>
                        <h3>{cocktail.name}</h3>
                        <p>{cocktail.description}</p>
                        <dl className="tasting-list">
                          <div>
                            <dt>Build</dt>
                            <dd>{cocktail.ingredients.join(" · ")}</dd>
                          </div>
                          <div>
                            <dt>Garnish</dt>
                            <dd>{cocktail.garnish || "Finished to suit the room"}</dd>
                          </div>
                          <div>
                            <dt>Timing</dt>
                            <dd>{cocktail.category === "clarified" ? "Advance preparation required" : "Crafted live for your event"}</dd>
                          </div>
                        </dl>
                      </div>
                      <button
                        type="button"
                        className={`quote-toggle flip-brief-button${inBrief ? " is-selected" : ""}`}
                        onClick={() => onToggleQuote(cocktail.id)}
                        data-testid={`cocktail-quote-btn-${cocktail.id}`}
                        aria-pressed={inBrief}
                        tabIndex={unlocked && isFlipped ? 0 : -1}
                      >
                        {inBrief ? <Check size={14} /> : <span>+</span>}
                        {inBrief ? "In event brief" : "Add to brief"}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
